
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  Ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import {
  ContentComment,
  Course,
  CourseTrack,
  Instructor,
  School,
} from "../../../domain/entities/entities";
import Card from "../components/application/filters/Card.vue";
import course from "../components/application/Course.vue";
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import { useRoute, useRouter } from "vue-router";
import ContentFallback from "../components/application/moldure/ContentFallback.vue";
import ModalIntroductoryContent from "../components/application/ModalIntroductoryContent.vue";
import { ref } from "@vue/runtime-core";
import { RepositoryException } from "../../../domain/exceptions/exceptions";
import { dispatchErrorBox, toLocaleDateTime } from "../components/mixins";
import CommentsModal from "../components/application/DashboardComments/CommentsModal.vue";
import Comment from "../components/application/DashboardComments/Comment.vue";
import PaginationDefault from "../components/application/shop/PaginationDefault.vue";
import { updateQuery } from "../utils/routing_utils";
import { wrapLoading } from "../utils/wrap_loading";
import { dispatchError } from "../utils/error_handling";
import { getOrderFromURL } from "../utils/order_utils";
import SearchHeader from "../components/application/SearchHeader.vue";

export default defineComponent({
  components: {
    PaginationDefault,
    Comment,
    CommentsModal,
    Event,
    Card,
    course,
    ContentFallback,
    Carousel,
    Slide,
    CourseTrack,
    Navigation,
    Pagination,
    ModalIntroductoryContent,
    SearchHeader,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const openModalIntroductoryContent: Ref<boolean> = ref(false);

    const course = ref(new Course());

    const schoolsSearch: ComputedRef<School[]> = computed(
      () => store.getters["globalSearch_module/schools"]
    );

    const courseTracksSearch: ComputedRef<CourseTrack[]> = computed(
      () => store.getters["globalSearch_module/courseTracks"]
    );

    const coursesSearch: ComputedRef<Course[]> = computed(
      () => store.getters["globalSearch_module/courses"]
    );

    const coursesPage: ComputedRef<number> = computed(
      () => store.getters["globalSearch_module/coursesPage"]
    );

    const instructorSearch: ComputedRef<Instructor[]> = computed(
      () => store.getters["globalSearch_module/instructors"]
    );

    /** Todo: document this explaining where and how it's gonna be used. */
    const filterSettings = {
      itemsToShow: 5,
      itemsToScroll: 1,
      snapAlign: "start",
      transition: 800,
      wrapAround: false
    };

    /** Todo: explain what the heck is this. */
    const filterBreakpoints = {
      250: { itemsToShow: 1, itemsToScroll: 1 },
      570: { itemsToShow: 2, itemsToScroll: 1 },
      900: { itemsToShow: 3, itemsToScroll: 1 },
      1100: { itemsToShow: 4, itemsToScroll: 4 },
      1350: { itemsToShow: 5, itemsToScroll: 5 },
      1630: { itemsToShow: 7, itemsToScroll: 7 },
      1870: { itemsToShow: 8, itemsToScroll: 8 },
    };

    const showEntity = (): number => {
      return (
        schoolsSearch.value.length ||
        courseTracksSearch.value.length ||
        coursesSearch.value.length ||
        instructorSearch.value.length
      );
    };

    const goToSchoolDetails = async (id: number) => {
      // await router.push({},`/details-course-tracks/${String(id)}`);
      await router.push({
        name: "details_course_tracks",
        query: {
          search: route.query.search,
        },
        params: {
          entityID: id,
        },
      });
    };

    const goToCourseTrackDetails = async (id: number) => {
      await router.push({
        name: "details_courses",
        query: {
          search: route.query.search,
        },
        params: {
          entityID: id,
        },
      });
    };

    const goToInstructorDetails = async (id: number) => {
      await router.push({
        name: "details_courses_instructors",
        query: {
          search: route.query.search,
        },
        params: {
          entityID: id,
        },
      });
    };

    const openIntroductoryContent = (_course: Course): void => {
      course.value = _course;
      openModalIntroductoryContent.value = true;
    };

    const closeModalIntroductoryContent = (): void => {
      openModalIntroductoryContent.value = false;
    };

    const isCommentsModalOpen: ComputedRef<boolean> = computed(
      () => store.getters["course_module/isCommentsModalOpen"]
    );

    /** Disable the body overflow to not let the user scroll the page behind some modal while is open. */
    const disableBodyOverflow = () => {
      document.body.style.overflow = "hidden";
    };

    /** Enable the body overflow to let the user scroll the page after close some modal. */
    const enableBodyOverflow = () => {
      document.body.style.overflow = "auto";
    };

    /** Dispatch an action to open the comment modal on screen. */
    const openCommentsModal = async () => {
      disableBodyOverflow();
      await store.dispatch("course_module/openCommentsModal");
    };

    /** Dispatch an action to open the comment modal on screen. */
    const closeCommentsModal = async () => {
      enableBodyOverflow();
      await store.dispatch("course_module/closeCommentsModal");
      await store.dispatch("course_module/clearComments");
    };

    /** Dispatch an action to load the comments. */
    const loadContentCommentsById = async (course: Course) =>
      await store.dispatch("course_module/loadContentCommentsById", course.id);

    /** Is what will be the title of the comment modal. */
    const commentModalTitle = ref("");

    /** Display the modal with the comments about the course. */
    const loadCourseCommentsModal = async (course: Course) => {
      try {
        commentModalTitle.value = course.title;
        await openCommentsModal();
        await loadContentCommentsById(course);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    };

    const comments: ComputedRef<ContentComment[]> = computed(
      () => store.getters["course_module/comments"]
    );

    const listCourses = async (): Promise<void> => {
      try {
        const order = getOrderFromURL(route);
        await wrapLoading(async () => {
          await store.dispatch("globalSearch_module/loadCourses", order);
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const getCoursesByInstructors = async (): Promise<void> => {
      try {
        const order = getOrderFromURL(route);
        await wrapLoading(async () => {
          await store.dispatch("globalSearch_module/loadInstructors", order);
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    /** Watches the route changes and remakes the request to list the interests. */
    watch(
      () => route.query.page,
      async () => {
        if (route.query.page) {
          await listCourses();
        }
      }
    );

    onBeforeMount(async () => {
      await listCourses();
    });

    function isCardBlocked(course) {
      return Boolean(course.isBlocked);
    }

    return {
      schoolsSearch,
      courseTracksSearch,
      instructorSearch,
      coursesSearch,
      filterSettings,
      filterBreakpoints,
      openModalIntroductoryContent,
      course,
      comments,
      isCommentsModalOpen,
      commentModalTitle,
      coursesPage,
      isCardBlocked,
      goToSchoolDetails,
      goToCourseTrackDetails,
      goToInstructorDetails,
      showEntity,
      openIntroductoryContent,
      closeModalIntroductoryContent,
      loadCourseCommentsModal,
      closeCommentsModal,
      toLocaleDateTime,
      updateQuery,
    };
  },
});
